import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {TrainingPlanEditorPresenter, Views} from './TrainingPlanEditorPresenter';
import {CreateTrainingPlanApi} from '../network/CreateTrainingPlanApi';
import {classNames} from '../../../components/support/classnames';
import {UpdateTrainingPlanApi} from '../network/UpdateTrainingPlanApi';
import {Structure} from './structure/Structure';
import {Timeline} from './events/Timeline';
import {PresenterProps, withPresenter} from '../support/with_presenter';
import {Media} from './media/Media';
import {DeviceRequirementsInput} from '../../../../admin/js/form/device_requirements_input';
import {listDevices} from '../../../../admin/js/models/DeviceName';
import {DeviceRequirementRule} from '../models/DeviceRequirementRule';

interface OwnProps {
    id?: number;
    title?: string;
    description?: string | null;
    deviceRequirementRules?: DeviceRequirementRule[];
    isPublished: boolean;
    canEditOutline: boolean;
}

const TrainingPlanEditorComponent: FunctionComponent<OwnProps & PresenterProps<TrainingPlanEditorPresenter>> = observer(
    ({presenter, canEditOutline}) => {
        return (
            <div className="training-plan-editor">
                {presenter.errors.length > 0 && (
                    <div className="alert alert-warning ion-md-close" role="alert">
                        <p>Validation error, please check your training plan and try again</p>
                    </div>
                )}
                {presenter.errors.length === 0 && presenter.error && (
                    <div className="alert alert-warning ion-md-close" role="alert">
                        <p>Something went wrong, please try again</p>
                    </div>
                )}
                <div className="row">
                    <div className="col-xs-12">
                        <div
                            className={classNames('form-group', {
                                'has-error': presenter.getErrorForField('title') !== null,
                            })}
                        >
                            <label htmlFor="title">Title</label>
                            <input
                                className="form-control"
                                name="title"
                                type="text"
                                id="title"
                                value={presenter.title}
                                onChange={(e) => presenter.onChangeTitle(e.target.value)}
                            />
                            {presenter.getErrorForField('title') !== null && (
                                <span className="help-block">
                                    <strong>{presenter.getErrorForField('title')}</strong>
                                </span>
                            )}
                        </div>
                        <div
                            className={classNames('form-group', {
                                'has-error': presenter.getErrorForField('description') !== null,
                            })}
                        >
                            <label htmlFor="descriptionu">Description</label>
                            <textarea
                                className="form-control"
                                name="description"
                                id="description"
                                value={presenter.description}
                                onChange={(e) => presenter.onChangeDescription(e.target.value)}
                            />
                            {presenter.getErrorForField('description') !== null && (
                                <span className="help-block">
                                    <strong>{presenter.getErrorForField('description')}</strong>
                                </span>
                            )}
                        </div>
                        <div
                            className={classNames('form-group', {
                                'has-error': presenter.getErrorForField('device_requirements') !== null,
                            })}
                        >
                            <label htmlFor="descriptionu">Device requirements</label>
                            <DeviceRequirementsInput
                                value={presenter.deviceRequirementRules}
                                onChange={(deviceRequirementRules) =>
                                    presenter.onChangeDeviceRequirementRules(deviceRequirementRules)
                                }
                                devices={listDevices()}
                            />
                            {presenter.getErrorForField('device_requirements') !== null && (
                                <span className="help-block">
                                    <strong>{presenter.getErrorForField('device_requirements')}</strong>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <ul className="nav nav-tabs">
                            <li className={classNames({active: presenter.activeView === Views.Structure})}>
                                <a onClick={() => presenter.changeView(Views.Structure)}>Structure</a>
                            </li>
                            <li className={classNames({active: presenter.activeView === Views.Timeline})}>
                                <a onClick={() => presenter.changeView(Views.Timeline)}>Timeline</a>
                            </li>
                            <li className={classNames({active: presenter.activeView === Views.Media})}>
                                <a onClick={() => presenter.changeView(Views.Media)}>Media</a>
                            </li>
                        </ul>
                    </div>
                </div>
                {presenter.activeView === Views.Structure && (
                    <Structure canEditOutline={canEditOutline} onSubmit={() => presenter.onSubmit()} />
                )}
                {presenter.activeView === Views.Timeline && <Timeline onSubmit={() => presenter.onSubmit()} />}
                {presenter.activeView === Views.Media && <Media onSubmit={() => presenter.onSubmit()} />}
            </div>
        );
    },
);

export const TrainingPlanEditor = withPresenter<TrainingPlanEditorPresenter, OwnProps>(
    (props, component) =>
        new TrainingPlanEditorPresenter(
            props.title || '',
            props.description || '',
            props.deviceRequirementRules || [],
            props.isPublished || false,
            props.id
                ? new UpdateTrainingPlanApi(props.id)
                : new CreateTrainingPlanApi(component.organisationId, component.collectionId),
            component.blockInteractor,
            component.eventInteractor,
            component.chaptersInteractor,
            component.mediaItemsInteractor,
        ),
    TrainingPlanEditorComponent,
);
